import { FC } from 'react';
import {
  Box,
  Grid,
  Link,
  Typography,
} from '@material-ui/core';
import { ACCZIOM_CLIENT, ACCZIOM_ORG, UI_PADDING } from 'src/globals';
import PropTypes from 'prop-types';
import { getActiveDisplayName, getActiveMemberItemsPinned, getActiveType } from 'src/utils/getActiveOrgInfo';
import useAuth from 'src/hooks/useAuth';
import { getPossibleHrefs } from 'src/utils/filterUrlsByRole';
import { Link as RouterLink } from 'react-router-dom';

const SmallLink: FC<{ href: string; title: string; }> = ({ href, title }) => (
  <Box display="inline-block">
    <Link
      to={href}
      component={RouterLink}
      underline="hover"
      color="text.primary"
      fontSize={14}
      mb={0.5}
    >
      {title}
    </Link>
  </Box>
);

SmallLink.propTypes = {
  href: PropTypes.string,
  title: PropTypes.string
};

const BigLink: FC<{ href?: string; title: string; mt?: boolean; }> = ({ href, title, mt }) => (
  href ? (
    <Box display="inline-block">
      <Link
        to={href}
        component={RouterLink}
        underline="hover"
        color="text.primary"
        display="inline-block"
      >
        <Typography
          variant="h6"
          fontWeight="bold"
          color="text.primary"
          mb={1}
          sx={mt && { mt: 2 }}
        >
          {title}
        </Typography>
      </Link>
    </Box>
  ) : (
    <Typography
      variant="h6"
      fontWeight="bold"
      color="text.primary"
      mb={1}
      sx={mt && { mt: 2 }}
    >
      {title}
    </Typography>
  )
);

BigLink.propTypes = {
  href: PropTypes.string,
  title: PropTypes.string,
  mt: PropTypes.bool
};

const Footer: FC = () => {
  const auth = useAuth();
  const activeDisplayName = getActiveDisplayName();
  const possibleHrefs = auth.isAuthenticated ? getPossibleHrefs() : [];
  const itemsPinned = auth.isAuthenticated ? getActiveMemberItemsPinned() : [];

  return (
    <Box
      id="footer"
      sx={{
        borderTopColor: (theme) => theme.palette.divider,
        borderTopWidth: '1px',
        borderTopStyle: 'solid'
      }}
    >
      <Box
        sx={{
          px: UI_PADDING,
          display: 'flex',
          flexDirection: 'column',
          py: 5
        }}
      >
        {auth.isAuthenticated ? (
          <Grid container>
            {getActiveType(ACCZIOM_CLIENT, ACCZIOM_ORG) === ACCZIOM_ORG ? (
              <>
                <Grid
                  item
                  xs={3}
                >
                  <Box
                    display="flex"
                    flexDirection="column"
                  >
                    <BigLink
                      href="/analysis"
                      title="Data Analysis"
                    />
                    <BigLink
                      title="Business Profiling"
                      mt
                    />
                    <SmallLink
                      href="/bs/search"
                      title="Business Search"
                    />
                    <SmallLink
                      href="/bs/anzsicsuggestion"
                      title="ANZSIC Suggestion"
                    />
                    <SmallLink
                      href="/bs/favourites"
                      title="Favourites"
                    />
                    {possibleHrefs.filter((h) => h.startsWith('/deal')).length > 0 && (
                      <BigLink
                        title="Deals"
                        mt
                      />
                    )}
                    {possibleHrefs.includes('/deal/sale') && (
                      <SmallLink
                        href="/deal/sale"
                        title={`${activeDisplayName} Sales`}
                      />
                    )}
                    {possibleHrefs.includes('/deal/purchase') && (
                      <SmallLink
                        href="/deal/purchase"
                        title={`${activeDisplayName} Purchases`}
                      />
                    )}
                    {possibleHrefs.includes('/deal/referral') && (
                      <SmallLink
                        href="/deal/referral"
                        title={`${activeDisplayName} Referrals`}
                      />
                    )}
                    {possibleHrefs.includes('/deal/autodebit') && (
                      <SmallLink
                        href="/deal/autodebit"
                        title={`${activeDisplayName} Auto Debits`}
                      />
                    )}
                  </Box>
                </Grid>
                <Grid
                  item
                  xs={3}
                >
                  <Box
                    display="flex"
                    flexDirection="column"
                  >
                    {possibleHrefs.includes('/management/microservice') && (
                      <BigLink
                        href="/management/microservice"
                        title="Services"
                      />
                    )}
                    {possibleHrefs.includes('/management/asset') && (
                      <BigLink
                        href="/management/asset"
                        title="Assets"
                        mt
                      />
                    )}
                    {possibleHrefs.filter((h) => h.startsWith('/management/structure')).length > 0 && (
                      <BigLink
                        href="/management/structure"
                        title="Structure"
                        mt
                      />
                    )}
                    {possibleHrefs.filter((h) => h.startsWith('/management/network')).length > 0 && (
                      <BigLink
                        href="/management/network"
                        title="Network"
                        mt
                      />
                    )}
                    {possibleHrefs.includes('/management/file') && (
                      <BigLink
                        href="/management/file"
                        title="Files"
                        mt
                      />
                    )}
                    {possibleHrefs.filter((h) => h.startsWith('/management/assign')).length > 0 && (
                      <BigLink
                        href="/management/assign"
                        title="Assign"
                        mt
                      />
                    )}
                  </Box>
                </Grid>
                <Grid
                  item
                  xs={3}
                >
                  <Box
                    display="flex"
                    flexDirection="column"
                  >
                    {possibleHrefs.filter((h) => h.startsWith('/marketplace')).length > 0 && (
                      <BigLink
                        title="Marketplace"
                      />
                    )}
                    {possibleHrefs.includes('/marketplace/organization') && (
                      <SmallLink
                        href="/marketplace/organization"
                        title="Join Organization"
                      />
                    )}
                    {possibleHrefs.includes('/marketplace/microservice') && (
                      <SmallLink
                        href="/marketplace/microservice"
                        title="Buy Service"
                      />
                    )}
                    {possibleHrefs.includes('/marketplace/asset') && (
                      <SmallLink
                        href="/marketplace/asset"
                        title="Purchase Asset"
                      />
                    )}
                    {possibleHrefs.filter((h) => h.startsWith('/communication')).length > 0 && (
                      <BigLink
                        title="Communication"
                        mt
                      />
                    )}
                    {possibleHrefs.includes('/communication/external') && (
                      <SmallLink
                        href="/communication/external"
                        title="External Messages"
                      />
                    )}
                    {possibleHrefs.includes('/communication/internal') && (
                      <SmallLink
                        href="/communication/internal"
                        title="Internal Messages"
                      />
                    )}
                    {possibleHrefs.includes('/wallet') && itemsPinned.includes('wallet') && (
                      <BigLink
                        href="/wallet"
                        title="Wallet"
                        mt
                      />
                    )}
                    {possibleHrefs.filter((h) => h.startsWith('/configuration')).length > 0 && (
                      <BigLink
                        href="/configuration"
                        title="Configuration"
                        mt
                      />
                    )}
                  </Box>
                </Grid>
              </>
            ) : (
              <>
                <Grid
                  item
                  xs={3}
                >
                  <Box
                    display="flex"
                    flexDirection="column"
                  >
                    <BigLink
                      title="Business Profiling"
                    />
                    <SmallLink
                      href="/bs/search"
                      title="Business Search"
                    />
                    <SmallLink
                      href="/bs/anzsicsuggestion"
                      title="ANZSIC Suggestion"
                    />
                    <SmallLink
                      href="/bs/favourites"
                      title="Favourites"
                    />
                    {possibleHrefs.filter((h) => h.startsWith('/configuration')).length > 0 && (
                      <BigLink
                        href="/configuration"
                        title="Configuration"
                        mt
                      />
                    )}
                  </Box>
                </Grid>
                <Grid
                  item
                  xs={3}
                >
                  <Box
                    display="flex"
                    flexDirection="column"
                  >
                    {possibleHrefs.filter((h) => h.startsWith('/deal')).length > 0 && (
                      <BigLink
                        title="Deals"
                      />
                    )}
                    {possibleHrefs.includes('/deal/purchase') && (
                      <SmallLink
                        href="/deal/purchase"
                        title={`${activeDisplayName} Purchases`}
                      />
                    )}
                    {possibleHrefs.includes('/deal/autodebit') && (
                      <SmallLink
                        href="/deal/autodebit"
                        title={`${activeDisplayName} Auto Debits`}
                      />
                    )}
                    {possibleHrefs.filter((h) => h.startsWith('/management/network')).length > 0 && (
                      <BigLink
                        href="/management/network"
                        title="Network"
                        mt
                      />
                    )}
                    {possibleHrefs.includes('/management/file') && (
                      <BigLink
                        href="/management/file"
                        title="Files"
                        mt
                      />
                    )}
                  </Box>
                </Grid>
                <Grid
                  item
                  xs={3}
                >
                  <Box
                    display="flex"
                    flexDirection="column"
                  >
                    {possibleHrefs.filter((h) => h.startsWith('/marketplace')).length > 0 && (
                      <BigLink
                        title="Marketplace"
                      />
                    )}
                    {possibleHrefs.includes('/marketplace/organization') && (
                      <SmallLink
                        href="/marketplace/organization"
                        title="Join Organization"
                      />
                    )}
                    {possibleHrefs.includes('/marketplace/microservice') && (
                      <SmallLink
                        href="/marketplace/microservice"
                        title="Buy Service"
                      />
                    )}
                    {possibleHrefs.includes('/marketplace/asset') && (
                      <SmallLink
                        href="/marketplace/asset"
                        title="Purchase Asset"
                      />
                    )}
                    {possibleHrefs.filter((h) => h.startsWith('/communication')).length > 0 && (
                      <BigLink
                        title="Communication"
                        mt
                      />
                    )}
                    {possibleHrefs.includes('/communication/external') && (
                      <SmallLink
                        href="/communication/external"
                        title="External Messages"
                      />
                    )}
                    {possibleHrefs.includes('/wallet') && itemsPinned.includes('wallet') && (
                      <BigLink
                        href="/wallet"
                        title="Wallet"
                        mt
                      />
                    )}
                  </Box>
                </Grid>
              </>
            )}
            <Grid
              item
              xs={3}
            >
              <Box
                display="flex"
                flexDirection="column"
              >
                <BigLink
                  title="Partner Sites"
                />
                <SmallLink
                  href="https://community.accziom.com/"
                  title="Accziom Community"
                />
                <SmallLink
                  href="https://bsearchau.accziom.com/"
                  title="Business Search"
                />
                <SmallLink
                  href="https://accziom.com/"
                  title="ACCZIOM - putting you in control"
                />
                <SmallLink
                  href="https://quickbooks.intuit.com/"
                  title="QBO"
                />
                <SmallLink
                  href="https://www.xero.com/"
                  title="Xero"
                />
                <SmallLink
                  href="https://stripe.com/"
                  title="Stripe"
                />
                <SmallLink
                  href="https://etherscan.io/"
                  title="Etherscan"
                />
                <Link
                  to="/"
                  component={RouterLink}
                  underline="hover"
                  color="text.primary"
                  fontSize={14}
                  mt={3}
                >
                  <img
                    src="/static/logo.svg"
                    alt="Logo"
                    height="30"
                  />
                </Link>
              </Box>
            </Grid>
          </Grid>
        ) : (
          <Grid container>
            <Grid
              item
              xs={3}
            >
              <Box
                display="flex"
                flexDirection="column"
              >
                <BigLink
                  title="Marketplace"
                />
                <SmallLink
                  href="/marketplace/organization"
                  title="Organizations"
                />
                <SmallLink
                  href="/marketplace/microservice"
                  title="Services"
                />
                <SmallLink
                  href="/marketplace/asset"
                  title="Assets"
                />
              </Box>
            </Grid>
            <Grid
              item
              xs={3}
            >
              <Box
                display="flex"
                flexDirection="column"
              >
                <BigLink
                  title="Partner Sites"
                />
                <SmallLink
                  href="https://community.accziom.com/"
                  title="Accziom Community"
                />
                <SmallLink
                  href="https://bsearchau.accziom.com/"
                  title="Business Search"
                />
                <SmallLink
                  href="https://accziom.com/"
                  title="ACCZIOM - putting you in control"
                />
                <SmallLink
                  href="https://quickbooks.intuit.com/"
                  title="QBO"
                />
                <SmallLink
                  href="https://www.xero.com/"
                  title="Xero"
                />
                <SmallLink
                  href="https://stripe.com/"
                  title="Stripe"
                />
                <SmallLink
                  href="https://etherscan.io/"
                  title="Etherscan"
                />
              </Box>
            </Grid>
            <Grid
              item
              xs={3}
            />
            <Grid
              item
              xs={3}
            >
              <Link
                to="/"
                component={RouterLink}
                underline="hover"
                color="text.primary"
                fontSize={14}
                mt={3}
              >
                <img
                  src="/static/logo.svg"
                  alt="Logo"
                  height="30"
                />
              </Link>
            </Grid>
          </Grid>
        )}
      </Box>
    </Box>
  );
};

export default Footer;
